import { Button, Pagination, Spinner } from '~/ui';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useEffect, useState } from 'react';
import SelectedIcon from '~/assets/svg/newSvg/selected-icon.svg?react';
import FileIcon from '~/assets/svg/newSvg/file.svg?react';
import styles from './FilesQuillContainer.module.scss';
import classNames from 'classnames';

interface IImage {
  id: number;
  name: string;
  full_path: string;
  type: string;
}

interface IImageQuillProps {
  assetId: number;
  isOpen: boolean;
  onClose: () => void;
  handleInsertFile: (data: { full_path: string; name: string }[]) => void;
}

const FilesQuillContainer = ({
  assetId,
  isOpen,
  onClose,
  handleInsertFile,
}: IImageQuillProps) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<IImage[]>([]);
  const toastError = useToastError();

  const { data: files, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/documents', {
        params: { perPage: 9, page, asset_id: assetId },
      });
      return data;
    },
    queryKey: ['documents', page, assetId],
    onError: error => toastError(error),
    enabled: isOpen,
  });

  const handleSubmit = () => {
    if (!!selectedId.length) {
      const documentPath = selectedId.map(item => {
        return { full_path: item.full_path, name: item.name };
      });
      handleInsertFile(documentPath);
      onClose();
    }
  };

  const handleSelectImage = (item: IImage) => {
    setSelectedId(prev =>
      prev.some(selected => selected.id === item.id)
        ? prev.filter(selected => selected.id !== item.id)
        : [...prev, item]
    );
  };

  useEffect(() => {
    if (!isOpen) setSelectedId([]);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.photoContainer}>
      <div className={styles.headingWrapper}>
        <p className={styles.title}>{t('upload_files')}</p>
        <div className={styles.closeBtnWrapper}>
          {!!selectedId.length && (
            <Button
              text={t('discard')}
              className={styles.resetBtn}
              onClick={() => setSelectedId([])}
            />
          )}
          <Button
            text={t('close')}
            className={styles.closeBtn}
            onClick={onClose}
          />
        </div>
      </div>
      <div className={styles.imagesWrapper}>
        <ul className={!isLoading ? styles.imagesList : styles.loadingList}>
          {files && !isLoading ? (
            files.data.map((item: IImage) => (
              <li key={item.id} className={styles.imagesItem}>
                <button
                  type="button"
                  onClick={() => handleSelectImage(item)}
                  className={classNames(
                    styles.imageBtn,
                    selectedId.some(selected => selected.id === item.id) &&
                      styles.activeImage,
                    { [styles.fileBg]: item.type === 'xlsx' }
                  )}
                >
                  {item.type === 'xlsx' ? (
                    <>
                      <FileIcon />
                      <span className={styles.fileName}>{item.name}</span>
                    </>
                  ) : (
                    <img
                      src={item.full_path}
                      alt={item.name}
                      className={styles.image}
                    />
                  )}
                  {selectedId.some(selected => selected.id === item.id) && (
                    <SelectedIcon className={styles.selectedIcon} />
                  )}
                </button>
              </li>
            ))
          ) : (
            <Spinner />
          )}
        </ul>
        <Pagination
          pageCount={files?.last_page}
          onPageChange={(selectedPage: { selected: number }) =>
            setPage(selectedPage.selected + 1)
          }
        />
      </div>
      <Button
        text={t('confirm')}
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={!selectedId}
      />
    </div>
  );
};

export default FilesQuillContainer;
